import React, { useEffect, useState } from 'react';
import { getLanguage, bulkUpdateLanguage } from '../../../../services/language.services';
import { makeStyles } from '@mui/styles';
import { Button } from '@mui/material';
import { Language } from '../../../../constants/Constants';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import { tablePrimaryButtonSx } from '../../../../styles/tableButtonStyles';

const useStyles = makeStyles(() => ({
  textarea: {
    width: '100%',
    height: '300px',
    fontFamily: 'monospace',
    fontSize: '14px',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    resize: 'none',
  },
  errorText: {
    color: 'red',
    marginBottom: '10px',
  },
  saveButton: {
    padding: '10px 15px',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:disabled': {
      backgroundColor: '#ccc',
      cursor: 'not-allowed',
    },
  },
}));

const BulkUpdateLanguage = ({ isoCode, onBulkUploadComplete }) => {
  const classes = useStyles();
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { notifyMessage } = useNotifyMessage();

  const fetchLanguage = async () => {
    const response: any = await getLanguage(isoCode ?? '');
    if (response.status === 200) {
      setJsonString(JSON.stringify(response.data, null, 2));
    }
  };

  useEffect(() => {
    if (isoCode) {
      fetchLanguage();
    }
  }, [isoCode]);

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedJsonString = e.target.value;
    setJsonString(updatedJsonString);

    try {
      JSON.parse(updatedJsonString);
      setError(null);
    } catch (err) {
      setError(Language.invalidJson);
      console.error(Language.errorBulkUpload, err);
    }
  };

  const handleSubmit = async () => {
    // Try parsing the JSON first to catch invalid JSON errors
    try {
      const updatedData = JSON.parse(jsonString);

      const res: any = await bulkUpdateLanguage(isoCode, updatedData);
      if (res.status === 200) {
        notifyMessage(Language.updateLanguageSuccess, 'success');
        onBulkUploadComplete();
      } else if (res.status === 404) {
        setError(res.data || 'Error: 404 Resource Not Found');
      } else if (res.status === 409) {
        setError(res.data);
      } else {
        setError(null);
        onBulkUploadComplete();
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setError(Language.invalidJson);
      } else {
        console.error(`${Language.errorBulkUpload}:`, error);
        setError(Language.errorDuringBulkUpload);
      }
    }
  };

  return (
    <div>
      <textarea value={jsonString} onChange={handleJsonChange} className={classes.textarea} />
      {error && <div className={classes.errorText}>{error}</div>}
      <Button onClick={handleSubmit} variant='contained' size='small' disabled={!!error} sx={tablePrimaryButtonSx}>
        {Language.saveChanges}
      </Button>
    </div>
  );
};

export default BulkUpdateLanguage;
